import * as Sentry from '@sentry/react';

/**
 * First-touch marketing attribution.
 *
 * Ad networks tag the landing URL with campaign params — `utm_*` across
 * networks. Those params only exist on the *landing* pageload: the instant
 * the visitor reloads, completes a round-trip, or returns tomorrow, the URL
 * is clean again and the in-memory Sentry scope (where `setTag` lives) is
 * gone.
 *
 * So on first touch we copy what we see into `sessionStorage`, and on every
 * pageload we re-stamp the Sentry scope from there. That's what lets a later
 * pageview several documents after the click still carry the ad that brought
 * the user in — `setTag` alone can't bridge a document boundary.
 *
 * `sessionStorage` (not `localStorage`) deliberately scopes attribution to the
 * tab/visit: a later visit from a different ad starts clean rather than
 * inheriting a stale first touch. It survives reloads and same-origin
 * navigations — the gap `setTag` can't cover — but it does NOT cross origins.
 *
 * Scope note: the marketing site (`www.`) and the product app (`app.`) are now
 * separate origins (see docs/webapp.md). This module attributes the *marketing
 * visit* — every Sentry event, trace, and replay the marketing site emits
 * carries the `marketing.*` tags, so "which ad drove this session?" is
 * answerable within the `goalpost-www` document. Carrying attribution across the
 * origin hop into the product app (to tie an ad to the signup that converted)
 * is intentionally out of scope; if that's wanted later, thread the params
 * through the navigation itself (CTA query string or the OAuth `state`) rather
 * than storage, which won't follow across origins.
 */

const STORAGE_KEY = 'gp.attribution';

// The params we lift off the landing URL. Two families:
//   - `utm_*` — the cross-network convention (utm_source=LinkedIn, etc.).
//   - LinkedIn Campaign Manager's dynamic-parameter macros, which carry the
//     ids that pinpoint *which* ad drove the click: `campaign_id` / `ad_id` /
//     `creative_id`, plus the human-readable `campaign_name` / `account_name`
//     and `account_id`. These identify the advertiser's campaign/creative —
//     they're constant across everyone who sees the ad, not personal data.
//
// The exact param names are configurable in the ad's URL template, so this
// allowlist is the single knob to adjust if that template changes — add the
// new name here and it flows through to tags/context unchanged.
const TRACKED_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'campaign_id',
  'campaign_name',
  'ad_id',
  'creative_id',
  'account_id',
  'account_name',
] as const;

// The two synthetic fields we add at capture time, alongside TRACKED_PARAMS.
const DERIVED_KEYS = ['referrer', 'landing_path'] as const;

// Everything this module is allowed to read back out of storage. Filtering on
// load (not just on write) means a polluted storage key — or a stray future
// write site — can't inject arbitrary `marketing.*` tags into Sentry.
const STORED_KEYS = new Set<string>([...TRACKED_PARAMS, ...DERIVED_KEYS]);

type Attribution = Record<string, string>;

/**
 * Read attribution from sessionStorage if present, otherwise from the current
 * landing URL, persist the first touch, and stamp it onto the Sentry scope.
 * Idempotent and safe to call on every pageload. Called from main.tsx after
 * initSentry so the scope exists.
 */
export function captureAttribution(): void {
  // First touch wins: once we've recorded a source for this visit, a later
  // navigation that happens to carry stray params won't overwrite it.
  const stored = loadStored();
  const attribution = stored ?? readLandingAttribution();
  if (!attribution) return;

  if (!stored) save(attribution);
  applyToScope(attribution);
}

// Lift campaign params off the landing URL, falling back to an external
// referrer so a click that arrived without UTM tags still records its source.
// Returns null when there's nothing worth attributing.
function readLandingAttribution(): Attribution | null {
  const params = new URLSearchParams(window.location.search);
  const captured: Attribution = {};
  for (const key of TRACKED_PARAMS) {
    const value = params.get(key);
    if (value) captured[key] = value;
  }

  const hasParams = Object.keys(captured).length > 0;
  const referrer = externalReferrer();
  // An internal referrer is just our own navigation, not an acquisition
  // source — only a UTM/click-id param or an external referrer counts as a
  // touch worth recording.
  if (!hasParams && !referrer) return null;

  if (referrer) captured.referrer = referrer;
  captured.landing_path = window.location.pathname;
  return captured;
}

// document.referrer, but only when it points off our own origin.
function externalReferrer(): string | undefined {
  const { referrer } = document;
  if (!referrer) return undefined;
  try {
    if (new URL(referrer).origin === window.location.origin) return undefined;
    return referrer;
  } catch {
    return undefined;
  }
}

function loadStored(): Attribution | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const result: Attribution = {};
    for (const [key, value] of Object.entries(parsed)) {
      // Allowlist on read: only keys this module owns become `marketing.*`
      // tags, so a polluted storage key can't inject arbitrary tags.
      if (STORED_KEYS.has(key) && typeof value === 'string') result[key] = value;
    }
    return Object.keys(result).length > 0 ? result : null;
  } catch {
    return null;
  }
}

function save(attribution: Attribution): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // sessionStorage can throw (private mode, quota exhausted). Attribution is
    // best-effort telemetry — never let it break the page.
  }
}

function applyToScope(attribution: Attribution): void {
  // Each value lands twice under the same `marketing.<key>` name, individually
  // filterable in Sentry (e.g. `marketing.utm_campaign:...`): as a tag, which
  // rides every error and the replay search for the session, and as a scope
  // attribute, which rides every span, log, and metric. Scope tags do not
  // reach spans, so the attribute is what lets Trace Explorer slice the
  // `gp.ui.*` spans by campaign. The grouped context keeps the full set
  // together on the event detail for a quick read.
  const attributes: Record<string, string> = {};
  for (const [key, value] of Object.entries(attribution)) {
    Sentry.setTag(`marketing.${key}`, value);
    attributes[`marketing.${key}`] = value;
  }
  Sentry.setAttributes(attributes);
  Sentry.setContext('marketing', attribution);
}
